import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Button, Modal, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { UNSAFE_NavigationContext } from 'react-router-dom';

const normalizeFormValue = (value) => {
  if (value === undefined) {
    return undefined;
  }

  if (value === null || typeof value !== 'object') {
    return value;
  }

  if (value instanceof Date || typeof value.toJSON === 'function') {
    return value.toJSON();
  }

  const file = value.originFileObj || value;
  if (typeof File !== 'undefined' && file instanceof File) {
    return {
      lastModified: file.lastModified,
      name: file.name,
      size: file.size,
      type: file.type,
    };
  }

  if (Array.isArray(value)) {
    return value.map(normalizeFormValue);
  }

  return Object.keys(value)
    .sort()
    .reduce((normalized, key) => {
      const normalizedValue = normalizeFormValue(value[key]);

      if (normalizedValue !== undefined) {
        normalized[key] = normalizedValue;
      }

      return normalized;
    }, {});
};

const formSnapshot = (form) =>
  JSON.stringify(normalizeFormValue(form.getFieldsValue(true)));
const USER_CHANGE_WINDOW_MS = 100;

export const usePhdDirtyLeaveGuard = (form, resetKey, isFormMounted = true) => {
  const { t } = useTranslation();
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const [isDirty, setIsDirty] = useState(false);
  const [isPromptOpen, setIsPromptOpen] = useState(false);
  const [isSavePending, setIsSavePending] = useState(false);
  const allowNextNavigation = useRef(false);
  const baseline = useRef(null);
  const pendingNavigation = useRef(null);
  const lastUserChangeAt = useRef(0);
  const resetKeySignature = JSON.stringify(normalizeFormValue(resetKey));

  const markPristine = useCallback(() => {
    baseline.current = formSnapshot(form);
    allowNextNavigation.current = false;
    pendingNavigation.current = null;
    setIsDirty(false);
    setIsPromptOpen(false);
  }, [form]);

  useEffect(() => {
    if (
      !isFormMounted ||
      resetKeySignature === 'null' ||
      resetKeySignature === undefined
    ) {
      return undefined;
    }

    const frame = requestAnimationFrame(markPristine);

    return () => cancelAnimationFrame(frame);
  }, [isFormMounted, markPristine, resetKeySignature]);

  const retryPendingNavigation = useCallback(() => {
    const navigation = pendingNavigation.current;
    pendingNavigation.current = null;
    setIsPromptOpen(false);

    if (!navigation) {
      return false;
    }

    navigation.retry();
    return true;
  }, []);

  const cancelPendingNavigation = useCallback(() => {
    pendingNavigation.current = null;
    setIsPromptOpen(false);
  }, []);

  const discard = useCallback(() => {
    setIsDirty(false);
    retryPendingNavigation();
  }, [retryPendingNavigation]);

  const onSaveSuccess = useCallback(
    (fallbackNavigation) => {
      setIsSavePending(false);
      setIsDirty(false);

      if (!retryPendingNavigation()) {
        allowNextNavigation.current = true;
        fallbackNavigation();
      }
    },
    [retryPendingNavigation],
  );

  const clearFailedSave = useCallback(() => {
    setIsSavePending(false);
    pendingNavigation.current = null;
    setIsPromptOpen(false);
  }, []);
  const onSaveFailed = clearFailedSave;

  useEffect(() => {
    const markUserChange = (event) => {
      if (
        event.type === 'click' &&
        !event.target.closest(
          '.ant-checkbox-wrapper, .ant-picker, .ant-radio-wrapper, .ant-select, .ant-switch, .ant-upload, [role="option"]',
        )
      ) {
        return;
      }

      lastUserChangeAt.current = performance.now();
    };

    window.addEventListener('input', markUserChange, true);
    window.addEventListener('change', markUserChange, true);
    window.addEventListener('click', markUserChange, true);

    return () => {
      window.removeEventListener('input', markUserChange, true);
      window.removeEventListener('change', markUserChange, true);
      window.removeEventListener('click', markUserChange, true);
    };
  }, []);

  const onValuesChange = useCallback(() => {
    if (performance.now() - lastUserChangeAt.current > USER_CHANGE_WINDOW_MS) {
      return;
    }

    requestAnimationFrame(() => {
      if (baseline.current !== null) {
        setIsDirty(formSnapshot(form) !== baseline.current);
      }
    });
  }, [form]);

  const savePendingForm = useCallback(async () => {
    if (isSavePending) {
      return;
    }

    setIsSavePending(true);

    try {
      await form.validateFields();
      form.submit();
    } catch {
      clearFailedSave();
    }
  }, [clearFailedSave, form, isSavePending]);

  useEffect(() => {
    if (!isDirty) {
      return undefined;
    }

    const originalPush = navigator.push;
    const originalReplace = navigator.replace;
    const originalGo = navigator.go;
    const block = (retry) => {
      if (allowNextNavigation.current) {
        allowNextNavigation.current = false;
        retry();
        return;
      }

      if (pendingNavigation.current) {
        return;
      }

      pendingNavigation.current = { retry };
      setIsPromptOpen(true);
    };

    navigator.push = (to, state) => block(() => originalPush(to, state));
    navigator.replace = (to, state) => block(() => originalReplace(to, state));
    navigator.go = (delta) => block(() => originalGo(delta));

    return () => {
      navigator.push = originalPush;
      navigator.replace = originalReplace;
      navigator.go = originalGo;
    };
  }, [isDirty, navigator]);

  useEffect(() => {
    const beforeUnload = (event) => {
      if (!isDirty) {
        return;
      }

      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', beforeUnload);

    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [isDirty]);

  const modal = (
    <Modal
      open={isPromptOpen}
      centered
      title={t('Neuložené zmeny')}
      closable={false}
      mask={{ closable: false }}
      footer={
        <Space>
          <Button disabled={isSavePending} onClick={cancelPendingNavigation}>
            {t('Zrušiť')}
          </Button>
          <Button disabled={isSavePending} onClick={discard}>
            {t('Nie')}
          </Button>
          <Button
            type="primary"
            loading={isSavePending}
            onClick={savePendingForm}
          >
            {t('Áno')}
          </Button>
        </Space>
      }
    >
      {t('Uložiť zmeny pred odchodom?')}
    </Modal>
  );

  return { markPristine, modal, onSaveFailed, onSaveSuccess, onValuesChange };
};
